/**
 * Column Mappings Configuration - SINGLE SOURCE OF TRUTH
 * 
 * This file contains all Excel column → Database column mappings.
 * 
 * Used by:
 * - Frontend: UploadMatrixModal.js, excelParserWorker.js
 * - Backend: src/jobs/queue.js (requires from here)
 * 
 * Uses dual-export pattern for both ES Modules (frontend) and CommonJS (backend).
 */

const REQUIRED_COLUMNS = {
  'Line Number': 'line_number',
  'Line Tag #': 'line_number',
};

const KNOWN_COLUMNS = {
  ...REQUIRED_COLUMNS,
  
  // Action column for deletion during re-import (type 'd' to delete row)
  'Action': 'action',
  'action': 'action',
  
  // Core identification columns
  'Job Number': 'job_number',
  'ISO Sheet Number': 'iso_sheet_number',
  'Iso Sheet Number': 'iso_sheet_number',
  'P&ID': 'pnid',
  'PNID': 'pnid',
  'Fluid Code': 'fluid_code',
  'Test Package 1': 'test_package_1',
  'Test Package 2': 'test_package_2',
  'Testing Package 1': 'test_package_1',
  'Testing Package 2': 'test_package_2',
  'Test Package': 'test_package_1',
  'Type': 'udf_type',
  'UDF Type': 'udf_type',
  'Tags': 'udf_tag',
  'UDF Tag': 'udf_tag',
  'Area': 'area',
  'Priority': 'priority',
  
  // Size columns
  'Size': 'size_1',
  'Size 1': 'size_1',
  'Size1': 'size_1',
  'Size 2': 'size_2',
  'Size 3': 'size_3',
  
  // Material columns
  'Material': 'material_1',
  'Material 1': 'material_1',
  'Mat1': 'material_1',
  'Material 2': 'material_2',
  'Material 3': 'material_3',
  
  // Schedule columns
  'Schedule': 'schedule_1',
  'Schedule 1': 'schedule_1',
  'Sch': 'schedule_1',
  'Schedule 2': 'schedule_2',
  'Schedule 3': 'schedule_3',
  
  // Length columns
  'Length': 'length_1',
  'Length 1': 'length_1',
  'Length1': 'length_1',
  'Length 2': 'length_2',
  'Length 3': 'length_3',
  
  // Other dimensional columns
  'UM': 'um',
  'Quantity': 'udf_qty',
  'UDF Qty': 'udf_qty',
  
  // Computed material columns (Note: 'Mat' maps to computed 'mat' column, not 'material_1')
  'Mat': 'mat',
  'Bore': 'bore',
  'Mat Bore': 'mat_bore',
  'Matbore': 'mat_bore',
  'Client Contract Number': 'client_contract_number',
  
  // Computed footage columns
  'SB Ft': 'sb_ft',
  'SB Ft Installed': 'sb_ft_installed',
  'LB Ft': 'lb_ft',
  'LB Ft Installed': 'lb_ft_installed',
  
  // Organization columns
  'Sub System': 'sub_system',
  'System': 'system',
  'Sub Area': 'sub_area',
  'Activity Name': 'activity_name',
  'Activity Id': 'activity_id',
  'Activity ID': 'activity_id',
  'Fab Activity ID': 'fab_activity_id',
  'Fab Activity Id': 'fab_activity_id',
  'Fab Activity Name': 'fab_activity_name',
  'Extra Number': 'extra_number',
  'Baseline': 'baseline_or_extra',
  'Baseline Or Extra': 'baseline_or_extra',
  'Baseline or Extra': 'baseline_or_extra',
  'Extra Description': 'extra_description',
  
  // Shop/Fabrication columns
  'Fabrication Shop': 'fabrication_shop',
  'Load Number': 'load_number',
  'Load #': 'load_number',
  'Date Shipped': 'date_shipped',
  'Comments': 'comments',
  'Comment': 'comments',
  
  // Progress tracking columns
  'Total Shop Completion': 'total_shop_completion',
  'Shop Progress': 'total_shop_completion',
  'Received': 'received',
  'Transport To Installation Area': 'transport_to_installation_area',
  'Transport to Installation Area': 'transport_to_installation_area',
  'Erect Supports Hangers': 'erect_supports_hangers',
  'Erect Supports / Hangers': 'erect_supports_hangers',
  'Fit Up': 'fit_up',
  'Bolt Up Weld': 'bolt_up_weld',
  'Bolt Up / Weld': 'bolt_up_weld',
  'Bolt/Weld': 'bolt_up_weld',
  'Line Cleanup Hydro Test': 'line_cleanup_hydro_test',
  'Line Cleanup / Hydro Test': 'line_cleanup_hydro_test',
  'Heat Tracing': 'heat_tracing',
  'Insulation': 'insulation',
  'Insulation % Complete': 'insulation',
  
  // Insulation and Heat Tracing budget/earned hours
  'Budget Hours Insulation': 'budget_hours_insulation',
  'Budget Hrs Insulation': 'budget_hours_insulation',
  'Earned Hours Insulation': 'earned_hours_insulation',
  'Earned Hrs Insulation': 'earned_hours_insulation',
  'Budget Hours Heat Tracing': 'budget_hours_heat_tracing',
  'Budget Hrs Heat Tracing': 'budget_hours_heat_tracing',
  'Earned Hours Heat Tracing': 'earned_hours_heat_tracing',
  'Earned Hrs Heat Tracing': 'earned_hours_heat_tracing',
  
  'Total Field Completion': 'field_total_completion',
  'Field Total Completion': 'field_total_completion',
  
  // Budget columns
  'Budget Cost': 'budget_cost_cad',
  'Budget Cost CAD': 'budget_cost_cad',
  'Budget Hours': 'budget_hours',
  'Budget Hours Shop': 'budget_hours_shop',
  'Budget Hours Field': 'budget_hours_field',
  'Budget Dia In': 'budget_dia_in',
  'Budget Credit': 'budget_credit',
  'Shop Field Ratio': 'shop_field_ratio',
  
  // Earned columns
  'Earned Hours Shop': 'earned_hours_shop',
  'Earned Shop Hours': 'earned_hours_shop',
  'Earned Hours Field': 'earned_hours_field',
  'Earned Field Hours': 'earned_hours_field',
  'Earned Field Cost CAD': 'earned_field_cost_cad',
  'Earned Shop Dollars': 'earned_field_cost_cad',
  'Earned Field Dollars': 'earned_field_dollars',
  'Earned Credit Shop': 'earned_credit_shop',
  'Earned Credit Field': 'earned_credit_field',
  
  // Diameter columns
  'Budget Dia In Shop': 'budget_dia_in_shop',
  'Budget Shop Diameter Inch': 'budget_dia_in_shop',
  'Budget Dia In Field': 'budget_dia_in_field',
  'Budget Field Diameter Inch': 'budget_dia_in_field',
  'Earned Dia In Shop': 'earned_dia_in_shop',
  'Earned Shop Diameter Inch': 'earned_dia_in_shop',
  'Earned Dia In Field': 'earned_dia_in_field',
  'Earned Field Diameter Inch': 'earned_dia_in_field',
  
  // Computed cost columns
  'Budget Cost Shop CAD': 'budget_cost_shop_cad',
  'Budget Cost Shop': 'budget_cost_shop_cad',
  'Budget Cost Field CAD': 'budget_cost_field_cad',
  'Budget Cost Field': 'budget_cost_field_cad',
  
  // Computed earned hours columns
  'Earned Hours Transport': 'earned_hours_transport_to_installation_area',
  'Earned Hours Support': 'earned_hours_support',
  'Earned Hours Fit Up': 'earned_hours_fit_up',
  'Earned Hours Bolt/Weld': 'earned_hours_bolt_up_weld',
  'Earned Hours Line Cleanup': 'earned_hours_line_cleanup_hydro_test',
  
  // Earned credits computed columns
  'Earned Credits Transport': 'earned_credits_transport',
  'Earned Credits Fit Up': 'earned_credits_fit_up',
  'Earned Credits Support': 'earned_credits_support',
  'Earned Credits Bolt/Weld': 'earned_credits_bolt_weld',
  'Earned Credits Line Cleanup': 'earned_credits_line_cleanup',
  
  // Backend-only columns
  'P6 Project ID': 'p6_project_id',
  'Schedule ID': 'p6_project_id',
  'Cost Code': 'cost_code',
  'Client WBS': 'client_wbs',
  'LGL WBS Internal': 'lgl_wbs_internal',
  'Internal WBS': 'lgl_wbs_internal',
  'SOV': 'sov'
};

/**
 * Computed columns in tracker_piping that cannot be directly written to.
 * Most are PostgreSQL GENERATED ALWAYS columns. mat and mat_bore are
 * trigger-managed (fn_resolve_mat via lt_mat lookup table).
 */
const COMPUTED_COLUMNS = [
  'budget_credit',
  'earned_credit_shop',
  'budget_hours_shop',
  'budget_hours_field',
  'earned_hours_shop',
  'budget_dia_in_shop',
  'budget_dia_in_field',
  'earned_dia_in_shop',
  'earned_dia_in_field',
  'budget_cost_shop_cad',
  'budget_cost_field_cad',
  'earned_hours_transport_to_installation_area',
  'earned_hours_support',
  'earned_hours_fit_up',
  'earned_hours_bolt_up_weld',
  'earned_hours_line_cleanup_hydro_test',
  'earned_hours_insulation',
  'earned_hours_heat_tracing',
  'sb_ft',
  'lb_ft',
  'sb_ft_installed',
  'lb_ft_installed',
  'field_total_completion',
  'earned_credit_field',
  'earned_hours_field',
  'mat',
  'bore',
  'mat_bore',
  'earned_credits_transport',
  'earned_credits_fit_up',
  'earned_credits_support',
  'earned_credits_bolt_weld',
  'earned_credits_line_cleanup'
];

// CommonJS export for Node.js backend
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { REQUIRED_COLUMNS, KNOWN_COLUMNS, COMPUTED_COLUMNS };
}

// ES Module exports for frontend
export { REQUIRED_COLUMNS, KNOWN_COLUMNS, COMPUTED_COLUMNS };
